import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { legalConfig, useConfig } from '@entities/config/api';

import { ConsentContext } from '../model/consentContext';
import {
  ALLOW_ALL,
  DENY_ALL,
  knownCategories,
  needsDecision,
  readConsent,
  writeConsent,
  type ConsentCategory,
  type ConsentDecision,
  type StoredConsent,
} from '../model/consentStore';

export interface ConsentContextValue {
  /** Объявленные оператором необязательные категории. Пусто — выбора нет. */
  categories: ConsentCategory[];
  decision: ConsentDecision;
  /** Показывать ли баннер первого визита. */
  bannerOpen: boolean;
  /** Открыт ли диалог настроек. */
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  /** Сохранить решение. `scope` фиксируется автоматически. */
  decide: (decision: ConsentDecision) => void;
  acceptAll: () => void;
  denyAll: () => void;
  /** Единственная точка, через которую подключается необязательная технология. */
  isAllowed: (category: ConsentCategory) => boolean;
  decidedAt: string | null;
}

/**
 * Управление согласием на необязательные технологии (§ 25 TDDDG).
 *
 * Провайдер стоит над всем приложением, потому что решение действует на всё
 * приложение, а не на страницу. Он **ничего не загружает сам**: он только
 * отвечает на вопрос «разрешено ли», а загрузку выполняет тот код, который
 * технологию подключает. Так согласие невозможно обойти, забыв про баннер.
 *
 * Сейчас `optional_cookie_categories` пуст: в проекте нет ни одного трекера,
 * поэтому баннер не показывается и решать нечего. Это не заглушка, а
 * правильное поведение — спрашивать согласие не на что.
 */
export function ConsentProvider({ children }: { children: ReactNode }) {
  const { data } = useConfig();
  // Читается один раз: значение живёт в `localStorage`, а не в состоянии
  // сервера, и перечитывать его на каждый рендер незачем.
  const [stored, setStored] = useState<StoredConsent | null>(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Пока конфигурация не пришла, объявленных категорий нет — и баннера тоже.
  // Это корректное состояние, а не заглушка: спрашивать согласие, не зная, на
  // что именно, нельзя.
  const declared = legalConfig(data)?.optional_cookie_categories;
  const categories = useMemo(() => knownCategories(declared ?? []), [declared]);

  const decide = useCallback(
    (decision: ConsentDecision) => {
      setStored(writeConsent(decision, categories));
      setSettingsOpen(false);
    },
    [categories],
  );

  const value = useMemo<ConsentContextValue>(() => {
    const decision = stored?.decision ?? DENY_ALL;
    return {
      categories,
      decision,
      bannerOpen: !settingsOpen && needsDecision(stored, categories),
      settingsOpen,
      openSettings: () => {
        setSettingsOpen(true);
      },
      closeSettings: () => {
        setSettingsOpen(false);
      },
      decide,
      acceptAll: () => {
        decide(ALLOW_ALL);
      },
      denyAll: () => {
        decide(DENY_ALL);
      },
      // Категория, которую оператор не объявил, запрещена независимо от
      // сохранённого решения: старое «да» не должно включать технологию,
      // добавленную позже.
      isAllowed: (category) => categories.includes(category) && decision[category],
      decidedAt: stored?.decidedAt ?? null,
    };
  }, [categories, decide, settingsOpen, stored]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
